import { useCallback } from 'react';
import { Skill, SkillRequirement, Job } from '../types';

interface GameState {
  activeEffects: { [effectId: string]: any };
  comboState: {
    lastSkillUsed: string | null;
    lastSkillTime: number;
    availableNextSkills: string[];
  };
  gauges: { [gaugeName: string]: number };
  resources: { [resourceName: string]: number };
  isInCombat: boolean;
}

export const useSkillHighlight = () => {
  
  // 检查单个条件是否满足
  const checkRequirement = useCallback((
    requirement: SkillRequirement, 
    gameState: GameState
  ): boolean => {
    switch (requirement.type) {
      case 'status':
        return checkStatusRequirement(requirement, gameState);
      
      case 'gauge':
        return checkGaugeRequirement(requirement, gameState);
      
      case 'combo':
        return checkComboRequirement(requirement, gameState);
      
      case 'proc':
        return checkProcRequirement(requirement, gameState);
      
      default:
        return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 检查状态效果条件
  const checkStatusRequirement = (
    requirement: SkillRequirement, 
    gameState: GameState
  ): boolean => {
    // 特殊处理战斗状态
    if (requirement.condition === 'in_combat') {
      return gameState.isInCombat;
    }
    
    const statusId = requirement.statusId || requirement.condition;
    const effect = gameState.activeEffects[statusId];
    if (!effect) return false;
    
    if (requirement.value !== undefined && requirement.operator) {
      return compareValues(effect.remainingTime, requirement.operator, requirement.value);
    }
    
    return effect.remainingTime > 0;
  };

  // 检查量谱条件
  const checkGaugeRequirement = (
    requirement: SkillRequirement, 
    gameState: GameState
  ): boolean => {
    const gaugeValue = gameState.gauges[requirement.condition] || 0;
    
    if (requirement.value !== undefined && requirement.operator) {
      return compareValues(gaugeValue, requirement.operator, requirement.value);
    }
    
    return gaugeValue > 0;
  };

  // 检查连击条件
  const checkComboRequirement = (
    requirement: SkillRequirement, 
    gameState: GameState
  ): boolean => {
    // requirement.condition 是当前技能ID，检查它是否在可用的下一步连击技能列表中
    return gameState.comboState.availableNextSkills.includes(requirement.condition);
  };

  // 检查概率触发条件
  const checkProcRequirement = (
    requirement: SkillRequirement, 
    gameState: GameState
  ): boolean => {
    return !!gameState.activeEffects[requirement.condition];
  };

  // 数值比较函数
  const compareValues = (
    value1: number, 
    operator: string, 
    value2: number
  ): boolean => {
    switch (operator) {
      case '>': return value1 > value2;
      case '>=': return value1 >= value2;
      case '<': return value1 < value2;
      case '<=': return value1 <= value2;
      case '=': return value1 === value2;
      case '!=': return value1 !== value2;
      default: return false;
    }
  };

  // 检查技能是否应该高亮
  const shouldHighlightSkill = useCallback((
    skill: Skill, 
    gameState: GameState
  ): boolean => {
    // 检查技能使用条件
    if (skill.requirements && skill.requirements.length > 0) {
      // 过滤掉战斗条件，因为战斗条件是基础使用条件，不应该影响高亮
      const highlightRequirements = skill.requirements.filter(req => 
        !(req.type === 'status' && req.condition === 'in_combat')
      );
      
      if (highlightRequirements.length === 0) {
        return false; // 如果只有战斗条件，不进行高亮
      }
      
      return highlightRequirements.every(req => checkRequirement(req, gameState));
    }
    
    return false;
  }, [checkRequirement]);

  // 检查技能是否可用
  const isSkillUsable = useCallback((
    skill: Skill, 
    gameState: GameState
  ): boolean => {
    // 检查使用条件（但combo类型的requirement不阻止使用，只影响高亮）
    if (skill.requirements && skill.requirements.length > 0) {
      const blockingRequirements = skill.requirements.filter(req => req.type !== 'combo');
      if (blockingRequirements.length > 0) {
        const requirementsMet = blockingRequirements.every(req => 
          checkRequirement(req, gameState)
        );
        if (!requirementsMet) return false;
      }
    }
    
    // 检查资源消耗
    if (skill.costs && skill.costs.length > 0) {
      return skill.costs.every(cost => {
        if (cost.type === 'status') {
          // 检查状态效果是否存在
          return !!gameState.activeEffects[cost.resource] && 
                 gameState.activeEffects[cost.resource].remainingTime > 0;
        } else {
          // 检查其他类型的资源
          const availableResource = gameState.resources[cost.resource] || 0;
          return availableResource >= cost.value;
        }
      });
    }
    
    return true;
  }, [checkRequirement]);

  // 获取技能的高亮类型
  const getHighlightType = useCallback((
    skill: Skill, 
    gameState: GameState
  ): 'none' | 'combo' | 'proc' | 'resource' | 'ready' => {
    if (!shouldHighlightSkill(skill, gameState)) {
      return 'none';
    }

    // 检查是否是连击高亮
    if (skill.requirements?.some(req => req.type === 'combo')) {
      return 'combo';
    }

    // 检查是否是状态触发高亮
    if (skill.requirements?.some(req => req.type === 'proc' || (req.type === 'status' && req.condition !== 'in_combat'))) {
      return 'proc';
    }

    // 检查是否是资源充足高亮
    if (skill.requirements?.some(req => req.type === 'gauge')) {
      return 'resource';
    }

    return 'ready';
  }, [shouldHighlightSkill]);

  // 获取高亮样式
  const getHighlightStyle = useCallback((highlightType: string) => {
    const baseStyle = {
      transition: 'all 0.3s ease',
      boxShadow: 'none'
    };

    switch (highlightType) {
      case 'combo':
        return {
          ...baseStyle,
          boxShadow: '0 0 10px #FFD700, inset 0 0 10px rgba(255, 215, 0, 0.3)',
          border: '2px solid #FFD700'
        };
      
      case 'proc':
        return {
          ...baseStyle,
          boxShadow: '0 0 10px #00FF00, inset 0 0 10px rgba(0, 255, 0, 0.3)',
          border: '2px solid #00FF00'
        };
      
      case 'resource':
        return {
          ...baseStyle,
          boxShadow: '0 0 10px #4A90E2, inset 0 0 10px rgba(74, 144, 226, 0.3)',
          border: '2px solid #4A90E2'
        };
      
      case 'ready':
        return {
          ...baseStyle,
          boxShadow: '0 0 8px #FFA500, inset 0 0 8px rgba(255, 165, 0, 0.3)',
          border: '2px solid #FFA500'
        };
      
      default:
        return baseStyle;
    }
  }, []);

  // 创建游戏状态对象的辅助函数
  const createGameState = useCallback((
    activeEffects: any,
    comboState: any,
    currentJob: Job | null,
    isInCombat: boolean = false
  ): GameState => {
    return {
      activeEffects,
      comboState,
      isInCombat,
      gauges: currentJob?.gauge ? 
        currentJob.gauge.states.reduce((acc, state) => {
          if (state.condition?.type === 'default') {
            state.components.forEach(component => {
              if (component.type === 'number') {
                acc[component.id] = component.current;
              }
            });
          }
          return acc;
        }, {} as Record<string, number>) : {},
      resources: {
        mp: 10000, // 示例MP值
        tp: 1000   // 示例TP值
      }
    };
  }, []);

  return {
    shouldHighlightSkill,
    isSkillUsable,
    getHighlightType,
    getHighlightStyle,
    createGameState,
    checkRequirement
  };
};